import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

// Milestone Achievements CRUD + the login celebration popup.
// Backend base path: /milestone-achievements
//  - list / create / update / delete follow the standard portal envelope
//    { status, message, data, meta }; `meta` only on the list.
//  - `my-due` and `mark-seen/:id` are scoped to the TOKEN, never to a query param,
//    so one employee can neither read nor dismiss another's milestones.
//  - milestones are COMPANY WIDE announcements: every employee is shown every recently
//    published milestone, whoever it belongs to, and each employee sees it exactly once.
//    "seen" is therefore tracked per (milestone, viewer) server side.

const initialState = {
  table: {
    loading: false,
    data: [],
    meta: {},
  },
  //ALL ACTIVE EMPLOYEES , FOR THE RECIPIENT AUTOCOMPLETE
  employees: {
    loading: false,
    data: [],
  },
  //MILESTONES THE LOGGED IN USER SHOULD BE CELEBRATED FOR RIGHT NOW
  due: {
    loading: false,
    data: [],
  },
  mutating: false,
};

export const getMilestoneAchievements = createAsyncThunk(
  'milestoneAchievement/getMilestoneAchievements',
  async ({ page = 1, limit = 10, search = '' } = {}) => {
    const searchParams = new URLSearchParams({ page, limit });
    if (search) searchParams.set('search', search);
    return apiRequest({ method: 'get', url: `milestone-achievements?${searchParams.toString()}` });
  },
);

export const getMilestoneEmployees = createAsyncThunk(
  'milestoneAchievement/getMilestoneEmployees',
  async () => apiRequest({ method: 'get', url: 'milestone-achievements/employees' }),
);

export const createMilestoneAchievement = createAsyncThunk(
  'milestoneAchievement/createMilestoneAchievement',
  async (body) => apiRequest({ method: 'post', url: 'milestone-achievements', data: body }),
);

export const updateMilestoneAchievement = createAsyncThunk(
  'milestoneAchievement/updateMilestoneAchievement',
  async (body) => apiRequest({ method: 'put', url: 'milestone-achievements', data: body }),
);

export const deleteMilestoneAchievement = createAsyncThunk(
  'milestoneAchievement/deleteMilestoneAchievement',
  async (milestoneId) => apiRequest({ method: 'delete', url: `milestone-achievements/${milestoneId}` }),
);

export const getMyDueMilestones = createAsyncThunk(
  'milestoneAchievement/getMyDueMilestones',
  async () => apiRequest({ method: 'get', url: 'milestone-achievements/my-due' }),
);

//DISMISSES THE WHOLE ANNOUNCEMENT IN ONE CALL — THE POPUP SHOWS EVERY DUE MILESTONE
//TOGETHER , SO THEY ARE MARKED SEEN TOGETHER. OMITTING THE IDS LETS THE SERVER DECIDE ,
//WHICH MEANS THE CLIENT CANNOT LEAVE A STRAGGLER BEHIND.
export const markMilestonesSeen = createAsyncThunk(
  'milestoneAchievement/markMilestonesSeen',
  async (milestoneIds) =>
    apiRequest({
      method: 'put',
      url: 'milestone-achievements/mark-seen',
      data: { milestoneIds: Array.isArray(milestoneIds) ? milestoneIds : [] },
    }),
);

const milestoneAchievementSlice = createSlice({
  name: 'milestoneAchievement',
  initialState,
  reducers: {
    //DROPS THE CELEBRATION QUEUE WITHOUT A ROUND TRIP , FOR LOGOUT
    clearDueMilestones: (state) => {
      state.due.data = [];
      state.due.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMilestoneAchievements.pending, (state) => {
        state.table.loading = true;
      })
      .addCase(getMilestoneAchievements.fulfilled, (state, action) => {
        state.table.loading = false;
        state.table.data = Array.isArray(action.payload?.data) ? action.payload.data : [];
        state.table.meta = action.payload?.meta ?? {};
      })
      .addCase(getMilestoneAchievements.rejected, (state) => {
        state.table.loading = false;
        state.table.data = [];
        state.table.meta = {};
      });

    builder
      .addCase(getMilestoneEmployees.pending, (state) => {
        state.employees.loading = true;
      })
      .addCase(getMilestoneEmployees.fulfilled, (state, action) => {
        state.employees.loading = false;
        state.employees.data = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(getMilestoneEmployees.rejected, (state) => {
        state.employees.loading = false;
        state.employees.data = [];
      });

    builder
      .addCase(getMyDueMilestones.pending, (state) => {
        state.due.loading = true;
      })
      .addCase(getMyDueMilestones.fulfilled, (state, action) => {
        state.due.loading = false;
        state.due.data = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(getMyDueMilestones.rejected, (state) => {
        state.due.loading = false;
        state.due.data = [];
      });

    //THE WHOLE ANNOUNCEMENT IS DISMISSED AT ONCE , SO THE QUEUE EMPTIES IN ONE GO
    builder.addCase(markMilestonesSeen.fulfilled, (state) => {
      state.due.data = [];
    });

    //ONE SHARED mutating FLAG ACROSS create / update / delete
    [createMilestoneAchievement, updateMilestoneAchievement, deleteMilestoneAchievement].forEach(
      (thunk) => {
        builder
          .addCase(thunk.pending, (state) => {
            state.mutating = true;
          })
          .addCase(thunk.fulfilled, (state) => {
            state.mutating = false;
          })
          .addCase(thunk.rejected, (state) => {
            state.mutating = false;
          });
      },
    );
  },
});

export const { clearDueMilestones } = milestoneAchievementSlice.actions;
export default milestoneAchievementSlice.reducer;
